import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { LogoMark } from '../components/Logo';
import { isPlatformAdminUser } from '../lib/platformAdmin';

const STEPS = [
  {
    key: 'job_function',
    label: 'Function',
    title: 'What best describes your function?',
    eyebrow: 'Step 1 of 3',
    options: ['Founder / CEO', 'Developer', 'Compliance / Legal', 'Operations'],
  },
  {
    key: 'use_case',
    label: 'Use case',
    title: 'What will you use idemGO for?',
    eyebrow: 'Step 2 of 3',
    options: ['Fintech / Bank', 'Marketplace', 'SaaS', 'Other'],
  },
  {
    key: 'volume',
    label: 'Volume',
    title: 'How many verifications do you expect per month?',
    eyebrow: 'Step 3 of 3',
    options: ['1–10', '10–100', '100–1000', '1000+'],
  },
];

function Progress({ current }) {
  return (
    <div className="w-full max-w-3xl" aria-label="Onboarding progress">
      <div className="flex items-center">
      {STEPS.map((step, index) => {
        const isCompleted = index < current;
        const isActive = index === current;
        return (
          <React.Fragment key={step.key}>
            <div className="flex items-center gap-2 min-w-0">
              <span className={`w-7 h-7 shrink-0 grid place-items-center border rounded-sm text-xs font-mono ${
                isActive
                  ? 'bg-zinc-900 border-zinc-900 text-white'
                  : isCompleted
                    ? 'bg-zinc-50 border-zinc-300 text-zinc-700'
                    : 'bg-white border-zinc-200 text-zinc-400'
              }`}>
                {isCompleted ? <Check className="w-3.5 h-3.5" strokeWidth={1.8}/> : index + 1}
              </span>
              <span className={`text-xs font-mono uppercase tracking-widest truncate ${
                isActive ? 'text-zinc-900' : isCompleted ? 'text-zinc-600' : 'text-zinc-400'
              }`}>
                {step.label}
              </span>
            </div>
            {index < STEPS.length - 1 && (
              <div className={`h-px flex-1 mx-3 ${isCompleted ? 'bg-zinc-300' : 'bg-zinc-200'}`} />
            )}
          </React.Fragment>
        );
      })}
      </div>
    </div>
  );
}

export default function Onboarding() {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [stepIndex, setStepIndex] = useState(0);
  const [answers, setAnswers] = useState({
    job_function: '',
    use_case: '',
    volume: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const step = STEPS[stepIndex];

  const choose = async (value) => {
    const nextAnswers = { ...answers, [step.key]: value };
    setAnswers(nextAnswers);
    setError(null);

    if (stepIndex < STEPS.length - 1) {
      setStepIndex(stepIndex + 1);
      return;
    }

    setSaving(true);
    try {
      await api.post('/onboarding', {
        job_function: nextAnswers.job_function,
        use_case: nextAnswers.use_case,
        volume: nextAnswers.volume,
      });
      await refresh();
      navigate(isPlatformAdminUser(user) ? '/internal' : '/dashboard', { replace: true });
    } catch (e) {
      setError(e?.response?.data?.error || 'Could not save onboarding.');
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <header className="h-16 border-b border-zinc-200">
        <div className="max-w-[1200px] mx-auto px-6 h-full flex items-center justify-between">
          <div className="flex items-center gap-2">
            <LogoMark size={25}/>
            <span className="font-display text-lg font-bold">idemGO</span>
          </div>
          <span className="text-xs font-mono text-zinc-500 truncate max-w-[220px]">
            {user?.organization_name || 'Workspace setup'}
          </span>
        </div>
      </header>

      <main className="flex-1 grid place-items-center px-6 py-12">
        <section className="w-full max-w-2xl">
          <div>
            <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">Workspace onboarding</p>
            <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Tell us a little about your workspace</h1>
            <p className="text-sm text-zinc-500 mt-2">This helps tailor the product experience. It does not affect permissions.</p>
          </div>

          <div className="mt-6">
            <Progress current={stepIndex}/>
          </div>

          <div className="mt-6 border border-zinc-200 rounded-sm p-6">
            <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">{step.eyebrow}</p>
            <h2 className="font-display text-xl font-semibold mt-1">{step.title}</h2>

            <div className="grid sm:grid-cols-2 gap-3 mt-6">
              {step.options.map((option) => {
                const selected = answers[step.key] === option;
                return (
                  <button
                    key={option}
                    type="button"
                    disabled={saving}
                    onClick={() => choose(option)}
                    className={`border rounded-sm px-4 py-4 text-left text-sm font-medium transition-colors disabled:opacity-50 ${
                      selected
                        ? 'border-black bg-zinc-900 text-white'
                        : 'border-zinc-200 text-zinc-800 hover:border-zinc-400'
                    }`}
                  >
                    {option}
                  </button>
                );
              })}
            </div>

            {error && <p className="mt-4 text-sm text-red-700">{error}</p>}

            <div className="mt-6 flex items-center justify-between">
              <button
                type="button"
                disabled={saving || stepIndex === 0}
                onClick={() => setStepIndex((current) => Math.max(0, current - 1))}
                className="text-sm text-zinc-600 hover:text-black disabled:opacity-40 disabled:hover:text-zinc-600"
              >
                Back
              </button>
              {saving && <span className="text-sm text-zinc-500">Saving...</span>}
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
